import type { RectsSignals } from '@fraudeai/schema';
import { defineModule } from '../harness.js';
import { attempt, round } from '../util/safe.js';
import { hashValue } from '../util/hash.js';

/**
 * Отпечаток по DOMRect.
 *
 * Дробные координаты элементов после трансформаций зависят от движка вёрстки,
 * системного DPI и округления субпикселей. Отдельный сигнал слабый, но
 * рандомизаторы обычно шумят и здесь — а значит, снова проверяем стабильность
 * повторных измерений.
 */

const ELEMENTS: string[] = [
  'width:33.7px;height:11.3px;transform:rotate(11.7deg) scale(1.031);',
  'width:47.11px;height:19.3px;transform:skewX(9.3deg) translateX(3.7px);',
  'width:5.5px;height:5.5px;transform:perspective(120px) rotateY(23.4deg);',
  'width:100.13px;height:0.5px;transform:matrix(1.02,0.03,0.07,0.98,1.7,2.3);',
  'width:12.3px;height:44.7px;padding:1.7px 3.3px;border:0.7px solid;',
  'font-size:13.7px;font-family:serif;width:auto;height:auto;',
];

interface Harness {
  read: () => number[];
  dispose: () => void;
}

function createHarness(): Harness | null {
  return attempt(() => {
    const holder = document.createElement('div');
    holder.style.cssText =
      'position:absolute;left:-9999px;top:-9999px;visibility:hidden;transform-style:preserve-3d;';

    const nodes: HTMLElement[] = ELEMENTS.map((style) => {
      const el = document.createElement('div');
      el.style.cssText = `position:absolute;${style}`;
      el.textContent = 'Ag Жф 01';
      holder.appendChild(el);
      return el;
    });

    document.body.appendChild(holder);

    return {
      read(): number[] {
        const values: number[] = [];
        for (const el of nodes) {
          const r = el.getBoundingClientRect();
          values.push(
            round(r.x, 6),
            round(r.y, 6),
            round(r.width, 6),
            round(r.height, 6),
            round(r.top, 6),
            round(r.right, 6),
            round(r.bottom, 6),
            round(r.left, 6),
          );
        }
        return values;
      },
      dispose() {
        attempt(() => holder.remove());
      },
    };
  });
}

export const rectsModule = defineModule({
  id: 'rects',
  phase: 1,
  timeoutMs: 3000,
  run(): RectsSignals {
    const harness = createHarness();
    if (!harness) {
      return { supported: false, hash: null, repeatHashes: null, stable: null, sample: null };
    }

    try {
      const repeatHashes: string[] = [];
      let sample: number[] | null = null;

      for (let i = 0; i < 3; i++) {
        const values = attempt(() => harness.read());
        if (!values) continue;
        if (!sample) sample = values.slice(0, 8);
        repeatHashes.push(hashValue(values));
      }

      return {
        supported: true,
        hash: repeatHashes[0] ?? null,
        repeatHashes,
        stable: repeatHashes.length > 1 ? new Set(repeatHashes).size === 1 : null,
        sample,
      };
    } finally {
      harness.dispose();
    }
  },
});
